import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	FormattedDateTime,
	InfoIconTooltipPopover,
	Row,
	SlateViewer,
	isEmptySlateValue,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DetailsPageInfoField } from "../../../../shared/ui/DetailsPageInfoField";
import { EventDetailResource } from "../../shared/types/event";
import { EventContextMenu } from "./EventContextMenu";

interface Props {
	event: EventDetailResource;
}

const GridCardBody = styled(CardBody)`
	display: grid;
	grid-gap: ${({ theme }) => theme.spacing(2)};
	grid-template-columns: 1fr;

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 1fr 1fr;
	}
`;

export const EventOverviewCard: FC<Props> = ({ event }) => {
	const { t } = useTranslation();

	return (
		<Card $variant="outlined">
			<CardHeader>
				<CardHeading>{t("Overview")}</CardHeading>
				<CardHeaderActions>
					<EventContextMenu event={event} />
				</CardHeaderActions>
			</CardHeader>
			<GridCardBody>
				<DetailsPageInfoField title={t("Start")} value={<FormattedDateTime dateTime={event.start} />} />
				<DetailsPageInfoField title={t("End")} value={<FormattedDateTime dateTime={event.end} />} />
				<DetailsPageInfoField title={t("Timezone")} value={event.timezone} />
				<DetailsPageInfoField title={t("Primary language")} value={event.primaryLanguage?.name} />
				{event.eventType.eventLanguageRequirement !== "prohibited" && (
					<DetailsPageInfoField
						title={t("Event languages")}
						value={event.languages.map((language) => language.name).join(", ") || "-"}
					/>
				)}
				<DetailsPageInfoField title={t("Event type")} value={event.eventType.name} />
				{event.eventType.locationRequirement !== "prohibited" && (
					<DetailsPageInfoField title={t("Location")} value={event.location?.name || "-"} />
				)}
				<DetailsPageInfoField title={t("Creator")} value={event.createdByUser?.name ?? "-"} />
				{event.eventType.organizerRequirement !== "prohibited" && (
					<DetailsPageInfoField
						title={t("Organiser")}
						value={
							event.organizer ? (
								<Row $columnGap={2}>
									{event.organizer.name}{" "}
									{event.organizer.description && !isEmptySlateValue(event.organizer.description) && (
										<InfoIconTooltipPopover>
											<SlateViewer content={event.organizer.description} />
										</InfoIconTooltipPopover>
									)}
								</Row>
							) : (
								"-"
							)
						}
					/>
				)}
				<DetailsPageInfoField title={t("Visibility")} value={t(`eventVisibility.${event.visibility}`)} />
				<DetailsPageInfoField
					title={t("Participation status")}
					value={
						event.participationStatus === "none" ? "-" : t(`eventParticipationStatus.${event.participationStatus}`)
					}
				/>
			</GridCardBody>
		</Card>
	);
};
